if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor () {
      super();
      this.params = {
        selectors: {
          item: '.advanced-grid__item',
          lastItem: '.advanced-grid__item:nth-last-child(5)',
          root: 'sticky-header',
        },
      };
      this.page = window.location.search;
      this.root = document.querySelector(this.params.selectors.root);
      this.loading = false;
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        rootMargin: '0px',
        threshold: 0.1,
      });
    }

    async connectedCallback () {
      await this.loadPrevPages();
      // Load more button mode (pagination "Load more button", data-mode="button"): no auto-loading on scroll
      if (this.dataset.mode === 'button') {
        this.setupLoadMore();
        return;
      }
      const lastItem = this.querySelector(this.params.selectors.lastItem);
      if (lastItem) {
        this.observer.observe(lastItem);
      }
    }

    disconnectedCallback () {
      this.observer.disconnect();
    }

    /**
     * *
     * @returns {number}
     */
    get page () {
      return this._page;
    }

    /**
     * *
     * @param queryParams
     */
    set page (queryParams) {
      const params = new URLSearchParams(queryParams);
      let currentPage = parseInt(params.get('page'));
      if (!currentPage || isNaN(currentPage)) {
        currentPage = 1;
      }
      this._page = currentPage;
    }

    /**
     * *
     * @param value
     */
    set loading (value) {
      if (this.root) {
        if (value) {
          this.root.classList.add('loading-bar');
        } else {
          this.root.classList.remove('loading-bar');
        }
      }
      this._loading = value;
    }

    /**
     * *
     * @returns {*}
     */
    get loading () {
      return this._loading;
    }

    /**
     * *
     * @param entries
     * @returns {Promise<void>}
     */
    async handleIntersect (entries) {
      const [entry] = entries;
      if (entry.isIntersecting && !this.loading) {
        this.loading = true;
        await this.loadNextPage();
        this.loading = false;
      }
    }

    /**
     * *
     * @param needlePage
     * @returns {string}
     */
    getUrl (needlePage = null) {
      const sectionId = this.closest('*[data-section]').getAttribute('data-section-id'),
        requestPage = needlePage !== null ? needlePage : this.page + 1,
        search = new URLSearchParams(window.location.search);
      search.set('section_id', sectionId);
      search.set('page', requestPage);
      return `${window.location.pathname}?${search.toString()}`;
    };

    /**
     * *
     * @param html
     * @returns {NodeListOf<Element>}
     */
    renderPrevPage (html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const items = doc.querySelectorAll(this.params.selectors.item);
      const firstItem = this.querySelector(this.params.selectors.item);
      if (firstItem) {
        items.forEach(item => {
          this.insertBefore(item, firstItem);
        });
      } else {
        items.forEach(item => {
          this.appendChild(item);
        });
      }
      return items;
    }

    /**
     * *
     * @param html
     * @returns {NodeListOf<Element>}
     */
    renderNextPage (html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const items = doc.querySelectorAll(this.params.selectors.item);
      items.forEach(item => {
        this.appendChild(item);
      });
      return items;
    }

    /**
     * *
     * @returns {Promise<void>}
     */
    async loadNextPage () {
      try {
        const url = this.getUrl();
        const response = await fetch(url);
        const html = await response.text();
        const items = this.renderNextPage(html);
        if (items.length) {

          const queryUrl = new URL(`${window.location.origin}/${url}`);
          const historyUrl = new URL(location);
          historyUrl.searchParams.set('page', queryUrl.searchParams.get('page'));
          this.page = queryUrl.searchParams;
          history.pushState({}, '', historyUrl);
          const lastItem = this.querySelector(this.params.selectors.lastItem);
          if (lastItem && this.dataset.mode !== 'button') {
            this.observer.observe(lastItem);
          }
        } else {
          this.done = true;
          this.observer.disconnect();
        }
      } catch (error) {
        console.error('Error loading next page:', error);
      }
    }

    /**
     * Load more button: "Showing X of Y", a progress line and the button (markup in main-collection-product-grid).
     * Hybrid: the first batch after page one loads by itself when the shopper nears the end.
     */
    setupLoadMore () {
      const box = this.parentElement && this.parentElement.querySelector('[data-sg-load-more]');
      if (!box) return;
      this.loadMoreBox = box;
      const button = box.querySelector('[data-sg-load-more-button]');
      this.updateLoadMore();
      // re-check the last row when the grid changes width (2 / 3 / 4 / 5 across)
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.updateLoadMore(), 200);
      });
      if (!button) return;
      const loadMore = async () => {
        if (this.loading || button.hidden) return;
        button.setAttribute('aria-busy', 'true');
        this.loading = true;
        await this.loadNextPage();
        this.loading = false;
        button.removeAttribute('aria-busy');
        this.updateLoadMore();
      };
      button.addEventListener('click', loadMore);
      // Hybrid: the first time the shopper nears the end, the next batch loads by itself; after that the button takes over
      if ('IntersectionObserver' in window) {
        const autoLoad = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          autoLoad.disconnect();
          loadMore();
        }, { rootMargin: '0px 0px 300px 0px' });
        autoLoad.observe(box);
      }
    }

    updateLoadMore () {
      const box = this.loadMoreBox;
      if (!box) return;
      const total = parseInt(box.dataset.total, 10) || 0;
      const perPage = parseInt(box.dataset.perPage, 10) || 1;
      const loaded = Math.min(this.page * perPage, total);
      const more = !this.done && loaded < total;
      const held = this.holdPartialRow(more);
      const shown = loaded - held;
      const status = box.querySelector('[data-sg-load-more-status]');
      const bar = box.querySelector('.sg-load-more__bar');
      const button = box.querySelector('[data-sg-load-more-button]');
      if (status) status.textContent = `Showing ${shown} of ${total}`;
      if (bar && total) bar.style.setProperty('--sg-load-more-progress', `${Math.round(shown / total * 100)}%`);
      if (button) button.hidden = !more;
    }

    /**
     * While more products are still to come, products in an unfinished last row are held back (hidden) so the grid
     * ends on a full row; they show first when the next page arrives. On the last page everything shows.
     * Returns how many products are held back.
     */
    holdPartialRow (more) {
      const items = [...this.querySelectorAll(this.params.selectors.item)];
      items.forEach((item) => item.classList.remove('sg-row-held'));
      if (!more || !items.length) return 0;
      const gridRight = this.getBoundingClientRect().right;
      const lastTop = Math.max(...items.map((item) => Math.round(item.getBoundingClientRect().top)));
      const lastRow = items.filter((item) => Math.abs(Math.round(item.getBoundingClientRect().top) - lastTop) < 2);
      const rowRight = Math.max(...lastRow.map((item) => item.getBoundingClientRect().right));
      if (gridRight - rowRight < 2) return 0;
      const products = lastRow.filter((item) => !item.classList.contains('advanced-grid__banner'));
      if (products.length === lastRow.length && products.length === items.length) return 0;
      products.forEach((item) => item.classList.add('sg-row-held'));
      return products.length;
    }

    /**
     * *
     * @returns {Promise<void>}
     */
    async loadPrevPages () {
      if (this.page > 1) {
        this.loading = true;
        let needleStep = this.page - 1;
        let pages = [];
        while (needleStep > 0) {
          pages.push(this.getUrl(needleStep));
          needleStep--;
        }
        const html = await Promise.all(pages.map(async url => {
          const resp = await fetch(url);
          return resp.text();
        }));
        html.forEach((item, index) => {
          this.renderPrevPage(item);
        });
        this.loading = false;
      }
    }

  }

  customElements.define('infinite-scroll', InfiniteScroll);
}